import { useEffect, useState } from "react";
import { View, Text, TouchableOpacity, ActivityIndicator } from "react-native";
import Svg, { Line, Polyline, Circle, Rect, Text as SvgText, G } from "react-native-svg";
import { apiRequest } from "../app/lib/api";

type Period = "day" | "month";
interface Point { date: string; count: number }

const NAVY = "#1B2A4A";
const ORANGE = "#F5A623";
const MUTED = "#9ca3af";

interface Props {
  title: string;
  endpoint: string;             // e.g. "/dashadmin/signups"
  unit?: string;                // e.g. "signup" or "dog" — singular noun for tooltip
  lineColor?: string;
}

export function TimeSeriesChart({ title, endpoint, unit = "signup", lineColor = ORANGE }: Props) {
  const [period, setPeriod] = useState<Period>("day");
  const [data, setData] = useState<Point[]>([]);
  const [loading, setLoading] = useState(true);
  const [width, setWidth] = useState(0);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    apiRequest<Point[]>(`${endpoint}?period=${period}`)
      .then((d) => setData(d))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [period, endpoint]);

  const total = data.reduce((s, p) => s + p.count, 0);

  const cw = width;
  const ch = 220;
  const pl = 36, pr = 16, pt = 20, pb = 30;
  const pw = Math.max(0, cw - pl - pr);
  const ph = ch - pt - pb;

  const counts = data.map((d) => d.count);
  const maxV = Math.max(1, ...counts);
  const niceMax = (() => {
    if (maxV <= 5) return 5;
    if (maxV <= 10) return 10;
    if (maxV <= 25) return 25;
    if (maxV <= 50) return 50;
    return Math.ceil(maxV / 50) * 50;
  })();

  const points = data.map((d, i) => {
    const x = pl + (data.length === 1 ? pw / 2 : (i / (data.length - 1)) * pw);
    const y = pt + ph - (d.count / niceMax) * ph;
    return { x, y, ...d };
  });

  const labelEveryN = period === "day" ? Math.ceil(data.length / 6) : Math.ceil(data.length / 8);
  const xLabels = points.filter((_, i) => i % labelEveryN === 0 || i === points.length - 1);
  const fmtX = (s: string) => {
    if (period === "day") {
      const d = new Date(s + "T00:00:00");
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
    const [y, m] = s.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  };

  return (
    <View style={{ backgroundColor: "#fff", borderRadius: 12, padding: 16, borderWidth: 1, borderColor: "#e5e7eb", marginBottom: 20 }}>
      <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <View>
          <Text style={{ fontSize: 14, fontWeight: "700", color: "#374151" }}>{title}</Text>
          <Text style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
            {total} total in last {period === "day" ? "90 days" : "24 months"}
          </Text>
        </View>
        <View style={{ flexDirection: "row", gap: 6 }}>
          {(["day", "month"] as Period[]).map((p) => (
            <TouchableOpacity
              key={p}
              onPress={() => setPeriod(p)}
              style={{
                paddingHorizontal: 12,
                paddingVertical: 6,
                borderRadius: 8,
                backgroundColor: period === p ? NAVY : "#f3f4f6",
              }}
            >
              <Text style={{ fontSize: 12, fontWeight: "600", color: period === p ? "#fff" : "#6b7280" }}>
                {p === "day" ? "By Day" : "By Month"}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View onLayout={(e) => setWidth(e.nativeEvent.layout.width)} style={{ width: "100%" }}>
        {loading ? (
          <View style={{ height: ch, alignItems: "center", justifyContent: "center" }}>
            <ActivityIndicator color={NAVY} />
          </View>
        ) : data.length === 0 ? (
          <View style={{ height: ch, alignItems: "center", justifyContent: "center" }}>
            <Text style={{ color: "#6b7280" }}>No data yet</Text>
          </View>
        ) : cw > 0 ? (
          <Svg width={cw} height={ch}>
            {[0, 1, 2, 3, 4].map((i) => {
              const v = (niceMax / 4) * i;
              const y = pt + ph - (v / niceMax) * ph;
              return <Line key={`g${i}`} x1={pl} x2={pl + pw} y1={y} y2={y} stroke="#f3f4f6" strokeWidth={1} />;
            })}
            {[0, 1, 2, 3, 4].map((i) => {
              const v = (niceMax / 4) * i;
              const y = pt + ph - (v / niceMax) * ph;
              return (
                <SvgText key={`l${i}`} x={pl - 6} y={y + 4} textAnchor="end" fontSize={10} fill={MUTED}>
                  {Math.round(v)}
                </SvgText>
              );
            })}

            <Polyline
              points={points.map((p) => `${p.x},${p.y}`).join(" ")}
              fill="none"
              stroke={lineColor}
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {points.map((p, i) => p.count > 0 ? (
              <Circle key={`p${i}`} cx={p.x} cy={p.y} r={activeIdx === i ? 5 : 3} fill={NAVY} />
            ) : null)}

            {points.map((p, i) => (
              <Circle
                key={`hit${i}`}
                cx={p.x}
                cy={p.y}
                r={12}
                fill="transparent"
                onPress={() => setActiveIdx(activeIdx === i ? null : i)}
                {...({ onMouseEnter: () => setActiveIdx(i), onMouseLeave: () => setActiveIdx(null) } as any)}
              />
            ))}

            {xLabels.map((p, i) => (
              <SvgText key={`x${i}`} x={p.x} y={ch - 10} textAnchor="middle" fontSize={10} fill={MUTED}>
                {fmtX(p.date)}
              </SvgText>
            ))}

            {activeIdx !== null && points[activeIdx] && (() => {
              const p = points[activeIdx];
              const dateLabel = fmtX(p.date);
              const countLabel = `${p.count} ${unit}${p.count === 1 ? "" : "s"}`;
              const tw = Math.max(80, Math.max(dateLabel.length, countLabel.length) * 7 + 20);
              const th = 38;
              const above = p.y - th - 10 > pt;
              const ty = above ? p.y - th - 10 : p.y + 10;
              const tx = Math.max(pl, Math.min(p.x - tw / 2, pl + pw - tw));
              return (
                <G>
                  <Rect x={tx} y={ty} width={tw} height={th} rx={6} fill={NAVY} />
                  <SvgText x={tx + tw / 2} y={ty + 16} textAnchor="middle" fontSize={11} fontWeight="600" fill="#ffffff">
                    {dateLabel}
                  </SvgText>
                  <SvgText x={tx + tw / 2} y={ty + 30} textAnchor="middle" fontSize={11} fontWeight="800" fill={lineColor}>
                    {countLabel}
                  </SvgText>
                </G>
              );
            })()}
          </Svg>
        ) : null}
      </View>
    </View>
  );
}

// Backward-compat default export so existing imports keep working
export function SignupsChart() {
  return <TimeSeriesChart title="Signups" endpoint="/dashadmin/signups" unit="signup" lineColor={ORANGE} />;
}
